import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { CategoryDrawer, CategoryList, CategorySidebar } from '@/components/catalog/CategorySidebar'
import { ModelDetailPanel } from '@/components/catalog/ModelDetailPanel'
import { ModelFilterBar } from '@/components/catalog/ModelFilterBar'
import { ModelList } from '@/components/catalog/ModelList'
import { useCatalogLayoutMode } from '@/hooks/useDeviceLayout'
import { modelsApi } from '@/lib/api/models'
import type { CatalogCategory, CatalogModel } from '@/types/catalog'
import { useCatalogStore } from '@/stores/catalogStore'
import { Button } from '@/components/ui/button'

export function CatalogLayout() {
  const layoutMode = useCatalogLayoutMode()
  const [categories, setCategories] = useState<CatalogCategory[]>([])
  const [models, setModels] = useState<CatalogModel[]>([])
  const [loading, setLoading] = useState(true)
  const [modelsReloadKey, setModelsReloadKey] = useState(0)
  const {
    selectedCategoryId,
    selectedModelId,
    mobileView,
    drawerOpen,
    filters,
    setSelectedCategoryId,
    selectModel,
    clearSelectedModel,
    setMobileView,
    openDrawer,
    closeDrawer,
    setFilters,
  } = useCatalogStore()

  useEffect(() => {
    let cancelled = false

    modelsApi
      .listCategories()
      .then((items) => {
        if (cancelled) return
        setCategories(items)
        const currentCategoryId = useCatalogStore.getState().selectedCategoryId
        const hasCurrent = items.some((item) => item.id === currentCategoryId)
        if ((!currentCategoryId || !hasCurrent) && items[0]) {
          setSelectedCategoryId(items[0].id)
        }
      })
      .catch(() => {
        if (!cancelled) setCategories([])
      })

    return () => {
      cancelled = true
    }
  }, [setSelectedCategoryId])

  useEffect(() => {
    if (!selectedCategoryId) return

    let cancelled = false
    setLoading(true)

    modelsApi
      .listModels({ categoryId: selectedCategoryId, filters, perPage: 200 })
      .then((result) => {
        if (!cancelled) setModels(result.items)
      })
      .catch(() => {
        if (!cancelled) setModels([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedCategoryId, filters, modelsReloadKey])

  const reloadModels = useCallback(() => {
    setModelsReloadKey((value) => value + 1)
  }, [])

  const selectedModel = useMemo(
    () => models.find((model) => model.id === selectedModelId) ?? null,
    [models, selectedModelId],
  )

  const handleSelectCategory = (categoryId: string) => {
    setSelectedCategoryId(categoryId)
    if (layoutMode === 'phone') {
      setMobileView('list')
    }
  }

  const handleSelectModel = (modelId: string) => {
    selectModel(modelId)
  }

  const loadingState = loading ? (
    <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
      Loading models…
    </div>
  ) : null

  if (layoutMode === 'phone') {
    if (mobileView === 'categories') {
      return (
        <div className="flex h-full flex-col" data-testid="catalog-layout">
          <header className="border-b border-border px-4 py-3">
            <h1 className="text-lg font-semibold">Model Catalog</h1>
          </header>
          <div className="min-h-0 flex-1 overflow-auto" data-testid="catalog-categories-stack">
            <CategoryList
              categories={categories}
              selectedCategoryId={selectedCategoryId}
              onSelect={handleSelectCategory}
            />
          </div>
        </div>
      )
    }

    if (mobileView === 'list') {
      return (
        <div className="flex h-full flex-col" data-testid="catalog-layout">
          <header className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileView('categories')}
              aria-label="Back to categories"
            >
              <ArrowLeft className="h-4 w-4" />
              Categories
            </Button>
            <h1 className="text-base font-semibold">Models</h1>
          </header>
          <ModelFilterBar filters={filters} onChange={setFilters} />
          {loadingState}
          <ModelList
            models={models}
            selectedModelId={selectedModelId}
            onSelect={handleSelectModel}
          />
        </div>
      )
    }

    return (
      <div className="flex h-full flex-col" data-testid="catalog-layout">
        <ModelDetailPanel
          model={selectedModel}
          showBackButton
          overlay
          onModelUpdated={reloadModels}
          onBack={() => {
            clearSelectedModel()
            setMobileView('list')
          }}
        />
      </div>
    )
  }

  if (layoutMode === 'tablet-portrait') {
    const showDetailOverlay = selectedModelId !== null

    return (
      <div className="flex h-full flex-col" data-testid="catalog-layout">
        <ModelFilterBar
          filters={filters}
          onChange={setFilters}
          showCategoriesButton
          onOpenCategories={openDrawer}
        />
        <div className="flex min-h-0 flex-1">
          {loadingState}
          <ModelList
            models={models}
            selectedModelId={selectedModelId}
            onSelect={handleSelectModel}
            className="w-full"
          />
          {!showDetailOverlay ? <ModelDetailPanel model={null} className="hidden" /> : null}
        </div>
        {showDetailOverlay ? (
          <ModelDetailPanel
            model={selectedModel}
            overlay
            showBackButton
            onModelUpdated={reloadModels}
            onBack={() => clearSelectedModel()}
          />
        ) : null}
        <CategoryDrawer
          open={drawerOpen}
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          onSelect={handleSelectCategory}
          onClose={closeDrawer}
        />
      </div>
    )
  }

  if (layoutMode === 'tablet-landscape') {
    return (
      <div className="flex h-full" data-testid="catalog-layout">
        <CategorySidebar
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          onSelect={handleSelectCategory}
          className="w-[200px]"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <ModelFilterBar filters={filters} onChange={setFilters} />
          <div className="flex min-h-0 flex-1">
            {loadingState}
            <ModelList
              models={models}
              selectedModelId={selectedModelId}
              onSelect={handleSelectModel}
              className="w-1/2 min-w-0"
            />
            <ModelDetailPanel
              model={selectedModel}
              className="w-1/2 min-w-0"
              onModelUpdated={reloadModels}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full" data-testid="catalog-layout">
      <CategorySidebar
        categories={categories}
        selectedCategoryId={selectedCategoryId}
        onSelect={handleSelectCategory}
        className="w-56"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <ModelFilterBar filters={filters} onChange={setFilters} />
        <div className="flex min-h-0 flex-1">
          {loadingState}
          <ModelList
            models={models}
            selectedModelId={selectedModelId}
            onSelect={handleSelectModel}
            className="w-[42%] min-w-0 border-r border-border"
          />
          <ModelDetailPanel
            model={selectedModel}
            className="min-w-0 flex-1"
            onModelUpdated={reloadModels}
          />
        </div>
      </div>
    </div>
  )
}
